import type {
  DocumentNode,
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  TypedDocumentNode,
} from '@apollo/client'
import { useQuery } from '@apollo/client'
import { useEffect, useMemo, useState } from 'react'

import { TotalOfItems } from '../schemas/types'
import usePagination from './usePagination'

type UsePaginatedQuery<TData> = QueryResult<TData, OperationVariables> & {
  pagination: ReturnType<typeof usePagination>
}
const usePaginatedQuery = <TData extends TotalOfItems>(
  query: DocumentNode | TypedDocumentNode<TData, OperationVariables>,
  options: QueryHookOptions<TData, OperationVariables>,
  filterChanged: string,
  itemsPerPage?: number
): UsePaginatedQuery<TData> => {
  const pagination = usePagination(filterChanged, itemsPerPage)
  const { limit, offset, setCount, rowsPerPage } = pagination

  const queryOptions = useMemo(
    () =>
      ({
        ...options,
        fetchPolicy: 'cache-first',
        variables: {
          limit,
          offset,
          ...options.variables,
        },
      }) as QueryHookOptions<TData>,
    [limit, offset, options]
  )
  const result = useQuery<TData>(query, queryOptions)

  const [prevData, setPrevData] = useState<number | null>(null)

  useEffect(() => {
    const totalSize = (result.data as TotalOfItems)?.total?.agg?.count || 0
    if (totalSize) {
      setCount(totalSize)
    }
  }, [setCount, result.data])

  // Refetch data when total count changes
  useEffect(() => {
    const observableQuery = result.client.watchQuery({
      query,
      variables: {
        limit,
        offset,
        ...options.variables,
      },
      fetchPolicy: 'network-only',
    })

    const subscription = observableQuery.subscribe(({ data: newData }) => {
      const newTotal = (newData as TotalOfItems)?.total?.agg?.count || 0
      if (newTotal && newTotal !== prevData) {
        setPrevData(newTotal)
        result.refetch()
      }
    })

    return () => subscription.unsubscribe()
  }, [result.client, query, limit, offset, options.variables, prevData, result.refetch])

  useEffect(() => {
    if ((result.data as TotalOfItems)?.total) {
      result.client.query({
        query,
        variables: {
          limit,
          offset: offset + rowsPerPage,
          ...options.variables,
        },
      })
    }
  }, [limit, offset, options.variables, query, result.client, rowsPerPage])

  return {
    ...result,
    pagination,
  }
}

export default usePaginatedQuery
